import { useEffect, useState } from "react";
import { MARKETING_URL } from "@/lib/appLink";

/**
 * Gate in front of the whole dashboard.
 *
 * The dashboard is a working surface built around wide tables, a fixed side
 * rail, and side-by-side panels. None of that survives a phone, and the
 * product does not have a phone layout, so a phone gets told so plainly
 * instead of being handed a broken one. The marketing site stays fully
 * responsive and is where a phone is sent.
 *
 * 767px is the last width below Tailwind's md breakpoint, which is where the
 * dashboard's own layouts start collapsing. A desktop window narrowed past
 * that point also lands here, and widening it puts the app straight back.
 */
const NARROW = "(max-width: 767px)";

const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(NARROW).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    // Rotating the device between mount and this effect would otherwise leave
    // the first render's answer in place.
    setNarrow(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return narrow;
};

/* Same shell as the login screen: centred column, logo, max-w-sm measure. */
const Notice = () => (
  <main className="min-h-screen bg-[#F8F9FA] text-ink flex" data-testid="desktop-only">
    <div className="flex-1 flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <img src="/Complaudax_logo.svg" alt="Complaudax" className="h-8 w-auto" />
        <h1 className="font-heading text-lg font-bold mt-6 mb-2">
          The dashboard is available on desktop only for now.
        </h1>
        <p className="text-sm text-ink-muted mb-6">
          Audits, the issue register, and the conformance reports are built around wide
          tables that need a larger screen. Open Complaudax on a computer to carry on.
        </p>
        <a
          href={MARKETING_URL}
          className="text-sm text-[#16324F] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm"
        >
          Go to complaudax.com
        </a>
      </div>
    </div>
  </main>
);

export const DesktopOnly = ({ children }) => (useIsNarrow() ? <Notice /> : children);
